import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { parseLocalDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Photo {
  id: number
  title: string | null
  document_date: string | null
  width: number | null
  height: number | null
  significant: number | null
  user_name: string | null
  thumbnail_url: string | null
  date_added: string | null
}

interface Props {
  case_record: CaseRecord
  photos: Photo[]
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = parseLocalDate(dateStr)
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
}

export default function Index({ case_record, photos }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Photos", href: `/cases/${case_record.id}/photos` },
  ]

  const basePath = `/cases/${case_record.id}/photos`

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this photo?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Photos - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {photos.length} Photo{photos.length !== 1 ? "s" : ""}
          </h1>
          <Link
            href={`${basePath}/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Photo
          </Link>
        </div>

        {photos.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="border-hpi-light-gray overflow-hidden rounded border"
              >
                <Link href={`${basePath}/${photo.id}`}>
                  <div className="bg-hpi-pale-gray flex aspect-square items-center justify-center">
                    {photo.thumbnail_url ? (
                      <img
                        src={photo.thumbnail_url}
                        alt={photo.title ?? `Photo #${photo.id}`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-hpi-med-gray text-sm">
                        No preview
                      </span>
                    )}
                  </div>
                </Link>
                <div className="p-2">
                  <div className="text-xs">
                    {formatDate(photo.document_date)}
                  </div>
                  {photo.width && photo.height && (
                    <div className="text-hpi-med-gray text-xs">
                      {photo.width}x{photo.height}
                    </div>
                  )}
                  {photo.title && (
                    <div className="text-hpi-med-gray mt-1 truncate text-xs">
                      {photo.title}
                    </div>
                  )}
                  {photo.significant === 1 && (
                    <div className="text-hpi-green mt-1 text-xs">
                      Final Report
                    </div>
                  )}
                  <div className="mt-2 flex gap-2 text-xs">
                    <Link
                      href={`${basePath}/${photo.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      View
                    </Link>
                    <Link
                      href={`${basePath}/${photo.id}/edit`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(photo.id)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-12 text-center">
            <p className="text-hpi-med-gray mb-4">No photos found.</p>
            <Link
              href={`${basePath}/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Photo
            </Link>
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
